import produce from 'immer';
import { useCallback } from 'react';
import { useMutation, useQuery } from 'react-query';

import { queryClient, request, toastError, useCurrentUserId } from '@/utils';

export const useClaimCode = ({ badgeId, enabled }: { badgeId: string; enabled?: boolean }) => {
  const url = '/official_badge/code/user_has_claimed';
  const currentAddress = useCurrentUserId();
  const select = useCallback((res: { has_claimed: boolean; code?: string }) => res.code, []);

  return useQuery(
    [url, badgeId, currentAddress],
    () => request.get<{ has_claimed: boolean; code?: string }>(url, { id: badgeId }),
    {
      select,
      enabled: enabled && !!currentAddress,
    },
  );
};

export const useGetClaimCode = (badgeId: string) => {
  const url = '/official_badge/code/claim';
  const currentAddress = useCurrentUserId();
  return useMutation(
    (token?: string) =>
      request.post<{ code: string; is_success: boolean }>(url, { id: badgeId, token }),
    {
      onSuccess: res => {
        if (res.code) {
          setClaimCode({
            badgeId: String(badgeId),
            currentAddress: currentAddress!,
            code: res.code,
          });
        }
      },
      onError(err: any) {
        toastError(err, 'failed to get claim code');
      },
    },
  );
};

const setClaimCode = ({
  badgeId,
  currentAddress,
  code,
}: {
  badgeId: string;
  currentAddress: string;
  code: string;
}) => {
  const queryKey = ['/official_badge/code/user_has_claimed', badgeId, currentAddress];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<{ code: string } | undefined>(queryKey, pre => {
      if (!pre) {
        return pre;
      }
      return produce(pre, draft => {
        draft.code = code;
      });
    });
  }
};
